import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X } from "lucide-react";
import { SITE } from "../data/site";

const LINKS = [
  { label: "Il Locale", href: "#locale" },
  { label: "Menù", href: "#menu" },
  { label: "Dove Siamo", href: "#dove" },
  { label: "Contatti", href: "#contatti" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-[100] transition-[background-color,backdrop-filter,border-color] duration-500 ${
          scrolled
            ? "bg-[#0A0A0A]/70 backdrop-blur-md border-b border-[var(--line)]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className="mx-auto max-w-[1600px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => go("#top")}
            className="flex items-center gap-3 group"
            aria-label="Dal Conte — torna in cima"
          >
            <img
              src="/assets/mark-white.png"
              alt="Dal Conte"
              className="h-8 md:h-9 w-auto transition-transform duration-500 group-hover:scale-105"
            />
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-serif text-lg md:text-xl tracking-wide">DAL CONTE</span>
              <span className="font-mono text-[9px] tracking-[0.35em] text-[var(--fg-dim)] uppercase mt-1">
                Pizzeria di Campagna
              </span>
            </span>
          </button>

          <div className="hidden lg:flex items-center gap-10">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-${l.href.replace("#", "")}`}
                onClick={() => go(l.href)}
                className="font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--fg-dim)] hover:text-[var(--fg)] transition-colors duration-300 link-underline"
              >
                {l.label}
              </button>
            ))}
            <a
              data-testid="nav-reserve"
              href={SITE.reserveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[11px] tracking-[0.2em] uppercase bg-[var(--fg)] text-[var(--bg)] px-6 py-3 hover:bg-transparent hover:text-[var(--fg)] border border-[var(--fg)] transition-colors duration-400"
            >
              Prenota
            </a>
          </div>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(true)}
            className="lg:hidden text-[var(--fg)]"
            aria-label="Apri menù"
          >
            <MenuIcon strokeWidth={1.2} size={28} />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[110] bg-[#0A0A0A] flex flex-col"
          >
            <div className="px-5 h-16 flex items-center justify-between border-b border-[var(--line)]">
              <span className="font-serif text-xl tracking-wide">DAL CONTE</span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Chiudi menù">
                <X strokeWidth={1.2} size={28} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.5 }}
                  onClick={() => go(l.href)}
                  className="text-left font-serif text-5xl py-3 border-b border-[var(--line)]"
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
            <a
              data-testid="mobile-reserve"
              href={SITE.reserveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="m-8 text-center font-mono text-sm tracking-[0.2em] uppercase bg-[var(--fg)] text-[var(--bg)] py-5"
            >
              Prenota un tavolo
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
